import * as React from "react";
import {
  DefaultNodeModel,
  DiagramEngine,
  BaseWidgetProps,
  DefaultLinkWidget,
  Toolkit,
  // LabelModel,
  // DefaultLabelModel
} from "storm-react-diagrams";
import { CustomLinkFactory } from "designer/widgets/CustomLinkFactory";

export interface DefaultNodeProps extends BaseWidgetProps {
  node: DefaultNodeModel;
  diagramEngine: DiagramEngine;
}

export interface DefaultNodeState {
  showAll: boolean;
}

export class CustomLinkWidget extends DefaultLinkWidget {
  generateMarker(id: string) {
    return (
      <defs>
        <marker
          id={id}
          markerWidth="8"
          markerHeight="8"
          refX="3.5"
          refY="3"
          orient="auto"
          markerUnits="strokeWidth"
        >
          <path d="M0,0 L0,6 L3.5,3 z" />
        </marker>
      </defs>
    );
  }

  tmpGenerateStartMarker(id: string) {
    return (
      <defs>
        <marker
          className={this.bem("-marker")}
          id={id}
          markerWidth="6"
          markerHeight="6"
          refX="-3"
          refY="3"
          orient="auto"
          markerUnits="strokeWidth"
        >
          <path d="M0,3 L4,4 L4,2 z" />
        </marker>
      </defs>
    );
  }

  tmpGenerateEndMarker(id: string) {
    const [p1, p2] = this.props.link.points;
    const angle = -90 + Math.atan2((p2.y - p1.y), (p2.x - p1.x)) / Math.PI * 180;
    return (
      <defs>
        <marker
          className={this.bem("-marker")}
          id={id}
          markerWidth="50"
          markerHeight="50"
          refX="4"
          refY="10"
          orient={angle}
          markerUnits="strokeWidth"
        >
          <polygon points="0,0 8,0 4,8"/>
        </marker>
      </defs>
    );
  }

  generateLink(
    path: string,
    extraProps: any,
    id: string | number,
    extraParams?: any
  ): JSX.Element {
    var props = this.props;
    var startMarkerId;
    var endMarkerId;
    var StartMarkerElement;
    var EndMarkerElement;
    var { startMarker, endMarker } = (this.props as any).link.markers || {
      startMarker: false,
      endMarker: true
    };

    if (startMarker === true) {
      startMarkerId = Toolkit.UID();
      StartMarkerElement = this.tmpGenerateStartMarker(startMarkerId);
    }
    if (endMarker === true) {
      endMarkerId = Toolkit.UID();
      EndMarkerElement = this.tmpGenerateEndMarker(endMarkerId);
    }
    //
    var Bottom = React.cloneElement(
      (props.diagramEngine.getFactoryForLink(
        this.props.link
      ) as CustomLinkFactory).generateLinkSegment(
        this.props.link as any,
        this,
        this.state.selected || this.props.link.isSelected(),
        path,
        startMarkerId,
        endMarkerId
      ),
      {
        ref: (ref: any) => ref && this.refPaths.push(ref)
      }
    );

    var Top = React.cloneElement(Bottom, {
      ...extraProps,
      markerStart: "",
      markerEnd: "",
      strokeLinecap: "round",
      onMouseLeave: () => {
        this.setState({ selected: false });
      },
      onMouseEnter: () => {
        this.setState({ selected: true });
      },
      ref: null,
      "data-linkid": this.props.link.getID(),
      strokeOpacity: this.state.selected ? 0.1 : 0,
      strokeWidth: 20,
      onContextMenu: () => {
        /*if (!this.props.diagramEngine.isModelLocked(this.props.link)) {
					event.preventDefault();
					this.props.link.remove();
				}*/
      }
    });

    return (
      <g key={"link-" + id}>
        {StartMarkerElement}
        {EndMarkerElement}
        {Bottom}
        {Top}
      </g>
    );
  }

  render() {
    const { diagramEngine } = this.props;
    if (!diagramEngine.nodesRendered) {
      return <g />;
    }

    // ensure id is present for all points on the path
    var points = this.props.link.points;
    var paths = [];

    if (this.isSmartRoutingApplicable()) {
      // first step: calculate a direct path between the points being linked
      const directPathCoords = this.pathFinding.calculateDirectPath(
        points[0],
        points[points.length - 1]
      );

      const routingMatrix = diagramEngine.getRoutingMatrix();
      // now we need to extract, from the routing matrix, the very first walkable points
      // so they can be used as origin and destination of the link to be created
      const smartLink = this.pathFinding.calculateLinkStartEndCoords(
        routingMatrix,
        directPathCoords
      );

      if (smartLink) {
        const { start, end, pathToStart, pathToEnd } = smartLink;

        // second step: calculate a path avoiding hitting other elements
        const simplifiedPath = this.pathFinding.calculateDynamicPath(
          routingMatrix,
          start,
          end,
          pathToStart,
          pathToEnd
        );

        paths.push(
          // smooth: boolean, extraProps: any, id: string | number, firstPoint: PointModel, lastPoint: PointModel
          this.generateLink(
            Toolkit.generateDynamicPath(simplifiedPath),
            {
              onMouseDown: (event: any) => {
                this.addPointToLink(event, 1);
              }
            },
            "0"
          )
        );
      }
    }

    // true when smart routing was skipped or not enabled.
    // See @link{#isSmartRoutingApplicable()}.
    if (paths.length === 0) {
      if (points.length === 2) {
        // var isHorizontal =
        //   Math.abs(points[0].x - points[1].x) >
        //   Math.abs(points[0].y - points[1].y);
        // var xOrY = isHorizontal ? "x" : "y";

        // draw the smoothing
        // if the points are too close, just draw a straight line
        // var margin = 50;
        // if (Math.abs(points[0][xOrY] - points[1][xOrY]) < 50) {
        //   margin = 5;
        // }

        var pointLeft = points[0];
        var pointRight = points[1];

        // some defensive programming to make sure the smoothing is
        // always in the right direction
        // if (pointLeft[xOrY] > pointRight[xOrY]) {
        //   pointLeft = points[1];
        //   pointRight = points[0];
        // }

        paths.push(
          this.generateLink(
            Toolkit.generateCurvePath(
              pointLeft,
              pointRight,
              this.props.link.curvyness
            ),
            {
              onMouseDown: (event: any) => {
                this.addPointToLink(event, 1);
              }
            },
            "0"
          )
        );

        // draw the link as dangeling
        if (this.props.link.targetPort === null) {
          paths.push(this.generatePoint(1));
        }
      } else {
        // draw the multiple anchors and complex line instead
        for (let j = 0; j < points.length - 1; j++) {
          paths.push(
            this.generateLink(
              Toolkit.generateLinePath(points[j], points[j + 1]),
              {
                "data-linkid": this.props.link.id,
                "data-point": j,
                onMouseDown: (event: MouseEvent) => {
                  this.addPointToLink(event, j + 1);
                }
              },
              j
            )
          );
        }

        // render the circles
        for (var i = 1; i < points.length - 1; i++) {
          paths.push(this.generatePoint(i));
        }

        if (this.props.link.targetPort === null) {
          paths.push(this.generatePoint(points.length - 1));
        }
      }
    }

    // paths = [
    //   ...paths,
    //   this.props.link.labels.map((label: any) => {
    //     return (
    //       <text key={label.id} ref={ref => (this.refLabels[label.id] = ref as any)}>
    //         {(label as DefaultLabelModel).label}
    //       </text>
    //     );
    //   })
    // ];

    this.refPaths = [];
    return (
      <g {...this.getProps()}>
        {paths}
        {this.props.link.labels.map((labelModel: any) => {
          return this.generateLabel(labelModel);
        })}
      </g>
    );
  }

  // calculateLabelPosition = (label: any, index: number) => {
  //   if (!this.refLabels[label.id]) {
  //     // no label? nothing to do here
  //     return;
  //   }

  //   // const { path, position } = this.findPathAndRelativePositionToRenderLabel(
  //   //   index
  //   // );

  //   const labelRef = this.refLabels[label.id];

  //   // const labelDimensions = {
  //   //   width: labelRef.clientWidth,
  //   //   height: labelRef.clientHeight
  //   // };

  //   // const pathCentre = path.getPointAtLength(position);

  //   const [p1, p2] = label.parent.points;

  //   const labelCoordinates = {
  //     x: (p1.x < p2.x ? p1.x + (p2.x - p1.x) / 2 : p2.x + (p1.x - p2.x) / 2) - 50,
  //     y: p1.y < p2.y ? p1.y + (p2.y - p1.y) / 2 : p2.y + (p1.y - p2.y) / 2
  //   };
  //   labelRef.setAttribute("x", labelCoordinates.x + "");
  //   labelRef.setAttribute("y", labelCoordinates.y + "");
  // };
}



// WEBPACK FOOTER //
// ./src/designer/widgets/CustomLinkWidget.tsx